// The dev-only pre-release used version 1 too. Convert only recognized aliases;
// never downgrade an unknown version or choose between conflicting keys.
export function convertPrereleaseConfiguration(value) {
  const config = structuredClone(typeof value === 'string' ? JSON.parse(value) : value);
  if (!config || config.schemaVersion !== 1) throw new Error('Unsupported configuration schema version.');
  for (const [oldKey, newKey] of Object.entries({nativeOptions: 'shopifyOptions', addonGroups: 'customOptions', includeItems: 'includedItems', packingItemOrder: 'includedItemOrder', switcherDraft: 'productSwitcherDraft'})) {
    if (!(oldKey in config)) continue;
    if (newKey in config) throw new Error(`Configuration contains both ${oldKey} and ${newKey}.`);
    config[newKey] = config[oldKey];
    delete config[oldKey];
  }
  if (config.settings?.formStyle === 'default') config.settings.formStyle = 'simple';
  if (config.settings?.formStyle === 'boxes') config.settings.formStyle = 'blocks';
  const optionType = (type) => ({nativeOption: 'shopifyOption', addonGroup: 'customOption'}[type] || type);
  if (Array.isArray(config.formOrder)) for (const item of config.formOrder) if (item) item.type = optionType(item.type);
  if (Array.isArray(config.customOptions)) for (const group of config.customOptions) {
    if (Array.isArray(group?.conditions?.rules)) for (const rule of group.conditions.rules) if (rule) rule.source = optionType(rule.source);
  }
  return config;
}

// Persist authored data, not the editor's Shopify display metadata.
export function authoredConfiguration(value) {
  const config = convertPrereleaseConfiguration(value);
  if (config.settings?.pageFeatures) {
    config.settings.packingListDisplay ??= config.settings.pageFeatures.packingList === false ? 'hidden' : 'belowImage';
    delete config.settings.pageFeatures.packingList;
    if (config.settings.pageFeatures.notices !== undefined) {
      config.settings.pageFeatures.disclaimers = config.settings.pageFeatures.notices;
      delete config.settings.pageFeatures.notices;
    }
  }
  for (const option of config.shopifyOptions || []) {
    delete option.name;
    delete option.position;
    for (const mapping of Object.values(option.values || {})) if (mapping?.includeItemIds?.length === 0) delete mapping.includeItemIds;
  }
  for (const group of config.customOptions || []) {
    if (group.status === 'active') delete group.status;
    for (const choice of group.choices || []) if (choice.includeItemIds?.length === 0) delete choice.includeItemIds;
  }
  if (config.noticeIds?.length === 0) delete config.noticeIds;
  const draft = config.productSwitcherDraft;
  if (draft?.value?.products) draft.value.products = draft.value.products
    .filter((item) => item.productId || item.label || item.description)
    .map(({productId, label, description}) => ({productId, label, ...(description === undefined ? {} : {description})}));
  return config;
}

export function configurationStructureErrors(config) {
  const errors = [];
  const object = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  if (!object(config)) return ['Configuration must be an object.'];
  if (config.schemaVersion !== 1) errors.push('Unsupported configuration schema version.');
  const objects = (value, path, optional = false) => {
    if (value === undefined && optional) return [];
    if (!Array.isArray(value) || value.some((entry) => !object(entry))) {
      errors.push(`${path} must be an array of objects.`);
      return [];
    }
    return value;
  };
  objects(config.formOrder, 'formOrder');
  const checkComponentGroup = (item) => {
    if (item.componentGroup !== undefined && typeof item.componentGroup !== 'string') errors.push('Component group must be text.');
  };
  objects(config.includedItems, 'includedItems').forEach(checkComponentGroup);
  if (config.includedItemOrder !== undefined && (!Array.isArray(config.includedItemOrder) || config.includedItemOrder.some((key) => typeof key !== 'string'))) errors.push('Included item order must be an array of strings.');
  for (const option of objects(config.shopifyOptions, 'shopifyOptions')) {
    if (!Array.isArray(option.optionValueIds) || option.optionValueIds.some((id) => typeof id !== 'string') || new Set(option.optionValueIds).size !== option.optionValueIds.length) errors.push('Shopify option value IDs must be a unique array of strings.');
    if (option.values !== undefined && !object(option.values)) errors.push('Shopify option values must be an object.');
  }
  for (const group of objects(config.customOptions, 'customOptions')) {
    if (group.conditions !== undefined) {
      if (!object(group.conditions)) errors.push('Conditions must be an object.');
      else for (const rule of objects(group.conditions.rules, 'Condition rules')) {
        if (!Array.isArray(rule.selectedIds)) errors.push('Condition selections must be an array.');
      }
    }
    for (const choice of objects(group.choices, 'Custom option choices')) {
      objects(choice.components, 'Choice components', true).forEach(checkComponentGroup);
      objects(choice.fields, 'Choice fields', true);
    }
  }
  return errors;
}
